import React, { useCallback } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Fab, Hidden, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import { Add, Delete, Remove } from '@mui/icons-material';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import { usePostChangeCartToolingQuantity } from 'services/Data/CartService';
import useImage from 'services/Hooks/useImage';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteBtn: {
    position: 'absolute',
    right: '0',
    top: '0',
  },
  squareFab: {
    borderRadius: 0,
    width: 36,
    height: 36,
    minHeight: 36,
  },
}));

const CartTooling = ({ tooling, handleOpenDeleteToolingModal, index }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { getToolingImage } = useImage();

  const { mutate: changeQuantity } = usePostChangeCartToolingQuantity();

  const handleQuantityChange = useCallback(
    quantity => {
      if (quantity <= 0) {
        handleOpenDeleteToolingModal(tooling);
      } else {
        changeQuantity({ ...tooling, quantity });
      }
    },
    [changeQuantity, tooling, handleOpenDeleteToolingModal],
  );

  return (
    <Box className={classes.pageFull}>
      <GridContainer>
        {index !== 0 && (
          <GridItem xs={12}>
            <Divider />
          </GridItem>
        )}
        <GridItem
          xs={12}
          md={3}
          sx={{ alignSelf: 'center', p: '0 !important', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignContent: 'center',
                flexDirection: 'row',
                justifyContent: 'space-evenly',
                height: '100%',
              }}>
              <GridItem xs={12} md={3} sx={{ alignSelf: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <ThemedTooltip key={`item-${tooling.id}`} title={t('removeFromCart')} placement={'top'}>
                    <Fab
                      color="error"
                      size="small"
                      className={classes.squareFab}
                      onClick={() => {
                        handleOpenDeleteToolingModal(tooling);
                      }}>
                      <Delete />
                    </Fab>
                  </ThemedTooltip>
                </Box>
              </GridItem>
              <GridItem md={9} lg={9} sx={{ alignSelf: 'center' }}>
                <Box className={classes.center}>
                  <img
                    src={tooling.imgSrc ? getToolingImage(tooling?.tooling_Id, tooling?.imgSrc.id) : '/images/brp-logo.png'}
                    alt="img"
                    style={{ maxWidth: '20vh', width: '100%' }}
                  />
                </Box>
              </GridItem>
            </Box>
          </Box>
        </GridItem>
        <GridItem
          xs={12}
          md={5}
          lg={4}
          sx={{ alignSelf: 'center', p: '0 !important', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="h1">{tooling.name}</Typography>

            <Divider sx={{ mt: 2, mb: 2 }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <Box>
                <Typography variant="h2">{t('unitPrice')}</Typography>
              </Box>
              <Box>
                <Typography fontSize={25} fontWeight="bold">
                  ${tooling?.price.toFixed(2)} {tooling.currencyName}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mt: 2, mb: 2 }} />
            {tooling.additionalToolings?.length > 0 &&
              tooling.additionalToolings.map(at => (
                <Typography fontSize={15} color="text.secondary" sx={{ mt: 1, mb: 1 }}>{`${at.quantity} X ${at.name ||
                  ''}`}</Typography>
              ))}
          </Box>
        </GridItem>
        <Hidden mdDown>
          <GridItem
            xs={12}
            md={2}
            lg={2}
            sx={{ alignSelf: 'center', p: '0 !important', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                fontSize={20}
                display="flex"
                justifyContent={'space-between'}
                alignItems={'center'}
                sx={{ width: '100%', maxWidth: '10vw' }}>
                <Fab
                  size="small"
                  color="secondary"
                  className={classes.squareFab}
                  disabled={tooling.quantity <= 0}
                  onClick={() => handleQuantityChange(tooling.quantity - 1)}>
                  <Remove />
                </Fab>
                <Typography>{tooling.quantity}</Typography>
                <Fab
                  size="small"
                  color="secondary"
                  className={classes.squareFab}
                  sx={{
                    color: '#000',
                  }}
                  disabled={tooling.quantity <= 0}
                  onClick={() => handleQuantityChange(tooling.quantity + 1)}>
                  <Add />
                </Fab>
              </Box>
            </Box>
          </GridItem>
        </Hidden>

        <GridItem xs={12} md={2} lg={3} sx={{ alignSelf: 'center', p: '0 !important' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              height: '100%',
            }}>
            <Hidden mdDown implementation="css">
              <Typography variant="h1">
                ${tooling?.subTotal.toFixed(2)} {tooling.currencyName}
              </Typography>
            </Hidden>
          </Box>
        </GridItem>
        <Hidden mdUp implementation="js">
          <GridItem xs={12} md={2.25} lg={3}>
            <Divider />

            <Box
              sx={{
                alignSelf: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: 'row',
                width: '100%',
                my: 3,
              }}>
              <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                <Box>
                  <Typography variant="h3">{t('quantity')}</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                <Typography variant="h3">{tooling.quantity}</Typography>
              </Box>
            </Box>
            <Divider />
            <Box
              xs={12}
              md={3}
              sx={{
                alignSelf: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: 'row',
                width: '100%',
                my: 3,
              }}>
              <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                <Box>
                  <Typography variant="h3">{t('price')}</Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                <Typography variant="h3">
                  ${tooling?.subTotal.toFixed(2)} {tooling.currencyName}
                </Typography>
              </Box>
            </Box>
          </GridItem>
        </Hidden>
      </GridContainer>
    </Box>
  );
};

export default CartTooling;
